import { Column, ColumnType, FieldType } from '../models/model'
import { formatDateByFormat, formatDateToDDMMYYYY } from '../services/common/common.service'
import { dateValidator } from '../validators/validators.validator'
import { GlobalConstants } from './global-constants'
import { COMPLETED, IN_PROGRESS, QUERIES_RECEIVED, QUERY_RECEIVED, StatsCard } from './stats-cards'

export const ACTIVITY_ID: Column = {
  name: 'Activity ID',
  key: 'displayId',
  type: ColumnType.STRING,
  sortByKey: 'displayId',
  cssClasses: ['rowColor'],
  filter: {
    key: 'displayId',
    type: FieldType.TEXT
  },
  anchor: {
    link: (data) => `/${GlobalConstants.ROUTING.ACTIVITY}/${data.id}/${GlobalConstants.ROUTING.DETAILS}`
  },
  build: (data: any) => data?.[ACTIVITY_ID.key]
}

export const ACTIVITY_NAME: Column = {
  name: 'Activity Name',
  key: 'name',
  type: ColumnType.STRING,
  sortByKey: 'name',
  cssClasses: ['rowColor'],
  filter: {
    key: 'name',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[ACTIVITY_NAME.key]
}

export const REQUEST_INITIATED_ON: Column = {
  name: 'Request Initiated On',
  key: 'createdDatetime',
  type: ColumnType.STRING,
  sortByKey: 'createdDatetime',
  cssClasses: ['rowColor'],
  filter: {
    key: 'createdDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[REQUEST_INITIATED_ON.key])
}

export const DAYS_ELAPSED: Column = {
  name: 'Days Elapsed',
  key: 'daysElapsed',
  type: ColumnType.NUMBER,
  cssClasses: ['rowColor'],
  build: (data: any) => data?.[DAYS_ELAPSED.key]
}

export const RESPONSE_DUE_BY: Column = {
  name: 'Response Due By',
  key: 'responseDueDatetime',
  type: ColumnType.STRING,
  sortByKey: 'responseDueDatetime',
  cssClasses: ['rowColor'],
  filter: {
    key: 'responseDueDatetime',
    type: FieldType.DATE
  },
  build: (data: any) => formatDateByFormat(data?.[RESPONSE_DUE_BY.key], GlobalConstants.DATE_FORMAT['DD/MM/YYYY'])
}

export const CURRENT_STATUS: Column = {
  name: 'Current Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  cssClasses: ['rowColor'],
  filter: {
    key: 'statusList',
    type: FieldType.SELECT,
    getOptionsList: (module) => {
      let options: StatsCard[]
      if (module == 'ACTIVITY') {
        options = GlobalConstants.ACTIVITY_STATUS_LIST
      } else if (module == 'QUERY') {
        options = [QUERIES_RECEIVED, IN_PROGRESS, COMPLETED]
      } else if (module == 'GB_TASK') {
        options = [QUERIES_RECEIVED, IN_PROGRESS, COMPLETED]
      }
      const optionsMap = new Map<string, string>()
      options.forEach((option) => optionsMap.set(option.key, option.label))

      return optionsMap
    }
  },
  build: (data: any) => data?.[CURRENT_STATUS.key]
}

export const QUERY_ID: Column = {
  name: 'Query ID',
  key: 'displayId',
  type: ColumnType.STRING,
  sortByKey: 'displayId',
  cssClasses: ['rowColor'],
  filter: {
    key: 'displayId',
    type: FieldType.TEXT
  },
  anchor: {
    link: (data) => `/${GlobalConstants.ROUTING.QUERY}/${data.id}/${GlobalConstants.ROUTING.DETAILS}`
  },
  build: (data: any) => data?.[QUERY_ID.key]
}

export const QUERY_NAME: Column = {
  name: 'Query Name',
  key: 'title',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  sortByKey: 'title',
  filter: {
    key: 'title',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[QUERY_NAME.key]
}

export const DESCRIPTION: Column = {
  name: 'Description',
  key: 'description',
  type: ColumnType.STRING,
  build: (data: any) => data?.[DESCRIPTION.key]
}

export const RAISED_ON: Column = {
  name: 'Raised On',
  key: 'createdDatetime',
  type: ColumnType.STRING,
  sortByKey: 'createdDatetime',
  cssClasses: ['rowColor'],
  filter: {
    key: 'createdDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[RAISED_ON.key])
}

export const HEADING: Column = {
  name: 'Article Title',
  key: 'heading',
  type: ColumnType.STRING,
  cssClasses: ['knowledge-title'],
  sortByKey: 'heading',
  filter: {
    key: 'heading',
    type: FieldType.TEXT
  },
  anchor: {
    link: (data) => `/${GlobalConstants.ROUTING.KNOWLEDGE_HUB}/${data.id}/${GlobalConstants.ROUTING.VIEW}`
  },
  build: (data: any) => data?.[HEADING.key]
}

export const KNOWLEDGE_CATEGORY: Column = {
  name: 'Category',
  key: 'category',
  type: ColumnType.STRING,
  sortByKey: 'category',
  filter: {
    key: 'category',
    type: FieldType.SELECT,
    getOptionsList: category => {
      return []
    }
  },
  build: (data: any) => data?.[KNOWLEDGE_CATEGORY.key]
}

export const QUERY_TASK_CURRENT_STATUS: Column = {
  name: 'Current Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  cssClasses: ['rowColor'],
  filter: {
    key: 'statusList',
    type: FieldType.SELECT_TASK_STATUS,
    getOptionsList: () => {
      let options: StatsCard[] = [QUERY_RECEIVED, IN_PROGRESS, COMPLETED]
      const optionsMap = new Map<string, string>()
      options.forEach((option) => optionsMap.set(option.key, option.label))
      return optionsMap
    }
  },
  build: (data: any) => data?.[QUERY_TASK_CURRENT_STATUS.key]
}

export const TASK_CATEGORY: Column = {
  name: 'Category',
  key: 'taskCategory',
  type: ColumnType.STRING,
  sortByKey: 'taskCategory',
  filter: {
    key: 'taskCategory',
    type: FieldType.SELECT,
    getOptionsList: module => {
      return []
    }
  },
  build: (data: any) => data?.[TASK_CATEGORY.key]
}
